import React, {useState, useEffect, useRef, useCallback} from 'react';
import PropTypes from 'prop-types';

import imageCompression from "browser-image-compression";
import "tui-image-editor/dist/tui-image-editor.css";
import TUImageEditor from "./TUImageEditor";

const ImageEditor = ({
  uploadGalleryStore,
  minImageWidthPixels,
  minImageHeightPixels,
  maxImageWidthPixels,
  maxImageHeightPixels,
  imageAspectRatio,
  editingErrors,
  setEditingErrors,
}) => {
  const editorRef = useRef();

  let editorInstance = null;

  const [editedImageURL, setEditedImageURL] = useState(""); // Edited image after transformations, typically a data url

  const [loading, setLoading] = useState(true);

  const transformImage = useCallback(async () => {
    let { url, file, name } = uploadGalleryStore.draftImage

    if (uploadGalleryStore.draftImageErrors.wrongAspectRatio) {
      url = await cropImage(url);
      file = await imageCompression.getFilefromDataUrl(url, name); // update file object for compression to use
    }

    if (uploadGalleryStore.draftImageErrors.largeFileSize) {
      url = await compressImage(file);
    } 

    if (uploadGalleryStore.draftImageErrors.largeResolution) {
      url = await decreaseImageResolution(url);
    }

    if (uploadGalleryStore.draftImageErrors.smallResolution) {
      url = await increaseImageResolution(url, minImageWidthPixels, minImageHeightPixels);
    }

    let editedImageBlob = await imageCompression.getFilefromDataUrl(url, name); // Returns blob actually...
    let editedImageFile = new File([editedImageBlob], name);
    uploadGalleryStore.setEditedImageFile(editedImageFile);

    setEditedImageURL(url);
    setLoading(false);
  }, [editedImageURL]);

  /**
   * @param {File} file
   * @returns {Promise<String | undefined>}
  */
  const compressImage = async (file) => {
    const options = {    
      maxSizeMB: 2,
      maxWidthOrHeight: Math.max(maxImageWidthPixels, maxImageHeightPixels),
      useWebWorker: true
    }

    try {
      const compressedImage = await imageCompression(file, options) 

      // The compression of the image will also handle the scaling down of the
      // image, so no need to later resize the image.
      const imageErrors = uploadGalleryStore.draftImageErrors
      if (imageErrors.largeResolution) {
        uploadGalleryStore.setDraftImageErrors({
          ...imageErrors,
          largeResolution: false
        })
      }

      // compressedImage is actually a Blob, so need to convert it to a File to
      // get data URL, and later have it uploaded.
      const compressedImageFile = new File([compressedImage], compressedImage.name);
      const compressedDataURL = await imageCompression.getDataUrlFromFile(compressedImageFile)

      return compressedDataURL;
    }
    catch(e) {
      setEditingErrors([
        ...editingErrors, 
        'Error compressing image, please try again.',
      ])
      console.error(e);
    }
  }

  async function cropImage(url) {
    await editorInstance.loadImageFromURL(url, "Cropped Image")

    let {width, height} = editorInstance.getCanvasSize();

    await editorInstance.crop({
      left: 0,
      top: Math.round(height/4),
      width: width, 
      height: Math.round(width / imageAspectRatio),
    });

    let { width: croppedWidth, height: croppedHeight } = editorInstance.getCanvasSize();
    let croppedDataUrl = editorInstance.toDataURL();

    // Check to make sure cropping didnt make file size of image larger
    let head = 'data:image/png;base64,';
    let imgFileSize = Math.round((croppedDataUrl.length - head.length) * 3 / 4) / 1024 / 1024
    if (imgFileSize > 2) {
      uploadGalleryStore.setDraftImageErrors({
        ...uploadGalleryStore.draftImageErrors,
        largeFileSize: true
      })
    }

    // Check in case the cropping made the image smaller than the min
    // thresholds, in which case set the small resolution flag to increase
    // resolution
    if (croppedWidth < minImageWidthPixels || croppedHeight < minImageHeightPixels) {
      let imageErrors = uploadGalleryStore.draftImageErrors
      uploadGalleryStore.setDraftImageErrors({
        ...imageErrors,
        smallResolution: true
      })
    }

    return croppedDataUrl;
  };

  const decreaseImageResolution = async (imageUrl) => {
    let img = await getImageObject(imageUrl);
    let canvas = document.createElement('canvas'),
    ctx = canvas.getContext("2d");

    let { naturalWidth: width, naturalHeight: height } = img

    let divider = 1;
    if (width >= height) {
      divider = width/maxImageWidthPixels
    }
    else {
      divider = height/maxImageHeightPixels
    }

    let targetWidth = Math.floor(width / divider );
    let targetHeight = Math.floor(height / divider);

    // destination canvas size
    canvas.width = targetWidth; 
    canvas.height = targetHeight;

    ctx?.drawImage(img, 0, 0, targetWidth, targetHeight);

    return canvas.toDataURL();
  }

  /**
   * @param {String} imageUrl data url of image that is being resized
   * @param {number} minWidth min width that the image will be resized to
   * @param {number} minHeight min height that the image will be resized to
   * @returns {Promise<String>} 
  */
  const increaseImageResolution = async (imageUrl, minWidth, minHeight) => {
    let img = await getImageObject(imageUrl)
    let canvas = document.createElement('canvas'),
    ctx = canvas.getContext("2d");

    let { naturalWidth: width, naturalHeight: height } = img

    // Calculate mutliplier to increase lowest dimension to reach min
    // requirement
    let multiplier = 1;
    if (width <= height) {
      multiplier = minWidth/width
    }
    else {
      multiplier = minHeight/height
    }

    let targetWidth = Math.floor(width * multiplier);
    let targetHeight = Math.floor(height * multiplier);

    // destination canvas size
    canvas.width = targetWidth; 
    canvas.height = targetHeight;

    ctx?.drawImage(img, 0, 0, targetWidth, targetHeight);

    let dataUrl = canvas.toDataURL();

    // Check for the (maybe rare) chance that the image is smaller than 10kb, if
    // so do recursive image resolution increase, increasinging min resolutions
    // by 0.5 each time, till we pass the 10kb requirement
    if(getDataUrlFileSize(dataUrl) < 0.010) {
      dataUrl = await increaseImageResolution(dataUrl, minWidth * 1.5, minHeight * 1.5);
    }

    return dataUrl;
  };


  /**
   * @param {String} url
   * @returns {Promise<HTMLImageElement>}
  */
  function getImageObject(url) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous"; // Get image with cors header to avoid tainted canvas errors

      img.onload = () => resolve(img);

      img.onerror = reject;

      img.src = url
    })
  }

  /**
   * Returns a rough estimation of the expected file size from a data url
   * @param {String} url
   * @returns {number}
  */
  function getDataUrlFileSize(url) {
    let head = 'data:image/png;base64,';
    let imgFileSize = Math.round((url.length - head.length) * 3 / 4) / 1024 / 1024;
    return imgFileSize;
  }

  // // When image editor is initially mounted, automatically transform draft image
  useEffect(() => {
    editorInstance = editorRef.current.getInstance();
    transformImage();
  }, [])

  return (
    <div>
      <div style={{ display: "none" }}>
        <TUImageEditor
          ref={editorRef}
          includeUI={{
            menuBarPosition: "bottom",
          }}
          cssMaxHeight={500}
          cssMaxWidth={700}
          usageStatistics={false}
        />
      </div>
      <div className="row" style={{minHeight: '250px'}}>
        <div className="col">
          {
            loading ? 
            <div className="skeleton-card-grid" style={{minHeight: '250px'}} />
            :
            <img
              src={editedImageURL}
              alt="Corrected Image"
              style={{ height: '250px', width: "100%", objectFit: "contain" }}
            />
          }
        </div>
        <div className="col d-flex flex-column justify-content-center">
          <p>This image has been corrected to meet listing provider size requirements.</p>
          <p>Add the corrected image to the listing?</p>
        </div>
      </div>
    </div>
  );
};

ImageEditor.propTypes = {
  uploadGalleryStore: PropTypes.object.isRequired,
  minImageWidthPixels: PropTypes.number,
  minImageHeightPixels: PropTypes.number,
  maxImageWidthPixels: PropTypes.number,
  maxImageHeightPixels: PropTypes.number,
  imageAspectRatio: PropTypes.number,
  aspectRatioPrettyReadable: PropTypes.string,
  editingErrors: PropTypes.array,
  setEditingErrors: PropTypes.func,
};

export default ImageEditor;
